import { ChartBar as BarChart3, BookOpen, CircleCheck as CheckCircle2, Clock, Trophy, ChevronRight } from 'lucide-react';
import type { CraftModuleRecord, UserCraftProgress } from '../../../types/craft';
import { CRAFT_MODULE_TABS } from '../../../types/craft';

interface Props {
  modules: CraftModuleRecord[];
  progress: UserCraftProgress[];
  onSelectModule: (slug: string) => void;
}

export default function CraftProgressDashboard({ modules, progress, onSelectModule }: Props) {
  const readyModules = modules.filter(m => m.status === 'completed');
  const totalTabs = readyModules.length * 10;
  const completedTabs = progress.filter(p => p.status === 'completed').length;
  const overallPercent = totalTabs > 0 ? Math.round((completedTabs / totalTabs) * 100) : 0;

  const modulesStarted = new Set(progress.map(p => p.module_slug)).size;
  const modulesCompleted = readyModules.filter(m => {
    const modTabs = progress.filter(p => p.module_slug === m.slug && p.status === 'completed');
    return modTabs.length >= 10;
  }).length;

  const inProgressModules = readyModules
    .map(m => {
      const modProgress = progress.filter(p => p.module_slug === m.slug);
      const done = modProgress.filter(p => p.status === 'completed').length;
      return { module: m, completed: done, total: 10 };
    })
    .filter(m => m.completed > 0 && m.completed < 10)
    .sort((a, b) => b.completed - a.completed);

  const completedModulesList = readyModules
    .filter(m => {
      const done = progress.filter(p => p.module_slug === m.slug && p.status === 'completed').length;
      return done >= 10;
    })
    .sort((a, b) => {
      const aDate = progress.find(p => p.module_slug === a.slug)?.completed_at || '';
      const bDate = progress.find(p => p.module_slug === b.slug)?.completed_at || '';
      return bDate.localeCompare(aDate);
    });

  const recentActivity = [...progress]
    .filter(p => p.completed_at)
    .sort((a, b) => (b.completed_at || '').localeCompare(a.completed_at || ''))
    .slice(0, 10);

  return (
    <div>
      <div className="bg-app-surface rounded-lg border border-app-line p-8 mb-8">
        <div className="max-w-2xl">
          <div className="flex items-center gap-2 mb-3">
            <BarChart3 className="w-5 h-5 text-app-positive" />
            <span className="text-xs font-semibold uppercase tracking-wider text-app-positive">Progress Tracker</span>
          </div>
          <h2 className="text-xl font-bold text-app-ink mb-4">My Learning Progress</h2>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard label="Overall Progress" value={`${overallPercent}%`} sub={`${completedTabs}/${totalTabs} tabs`} />
            <StatCard label="Modules Started" value={String(modulesStarted)} sub={`of ${readyModules.length}`} />
            <StatCard label="Modules Completed" value={String(modulesCompleted)} sub={`of ${readyModules.length}`} />
            <StatCard label="Tabs Completed" value={String(completedTabs)} sub="total" />
          </div>

          {totalTabs > 0 && (
            <div className="mt-5">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-app-muted">Overall Progress</span>
                <span className="text-xs text-app-muted">{overallPercent}%</span>
              </div>
              <div className="w-full h-2 bg-app-line rounded-full overflow-hidden" role="progressbar" aria-label="Overall learning progress" aria-valuemin={0} aria-valuemax={100} aria-valuenow={overallPercent}>
                <div
                  className="h-full bg-app-positive rounded-full transition duration-500"
                  style={{ width: `${overallPercent}%` }}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <h3 className="text-sm font-semibold text-app-ink mb-4 flex items-center gap-2">
            <Clock className="w-4 h-4 text-app-warning" />
            In Progress ({inProgressModules.length})
          </h3>
          {inProgressModules.length === 0 ? (
            <p className="text-xs text-app-muted py-4">No modules in progress. Start learning from the Library tab.</p>
          ) : (
            <div className="space-y-2">
              {inProgressModules.map(({ module: m, completed, total }) => (
                <button
                  key={m.slug}
                  onClick={() => onSelectModule(m.slug)}
                  className="w-full flex items-center gap-3 px-4 py-3 bg-app-surface border border-app-line rounded-lg hover:border-app-focus/30 hover:shadow-none transition group text-left"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-app-ink truncate group-hover:text-app-focus transition-colors">{m.title}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <div className="flex-1 h-1 bg-app-line rounded-full overflow-hidden" role="progressbar" aria-label={`${m.title} progress`} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round((completed / total) * 100)}>
                        <div
                          className="h-full bg-app-warning rounded-full"
                          style={{ width: `${(completed / total) * 100}%` }}
                        />
                      </div>
                      <span className="text-[10px] text-app-muted">{completed}/{total}</span>
                    </div>
                  </div>
                  <ChevronRight className="w-3.5 h-3.5 text-app-line-strong group-hover:text-app-focus flex-shrink-0" />
                </button>
              ))}
            </div>
          )}

          <h3 className="text-sm font-semibold text-app-ink mt-8 mb-4 flex items-center gap-2">
            <Trophy className="w-4 h-4 text-app-positive" />
            Completed ({completedModulesList.length})
          </h3>
          {completedModulesList.length === 0 ? (
            <p className="text-xs text-app-muted py-4">No modules completed yet.</p>
          ) : (
            <div className="space-y-2">
              {completedModulesList.map(m => (
                <button
                  key={m.slug}
                  onClick={() => onSelectModule(m.slug)}
                  className="w-full flex items-center gap-3 px-4 py-3 bg-app-surface border border-app-positive rounded-lg hover:shadow-none transition group text-left"
                >
                  <CheckCircle2 className="w-4 h-4 text-app-positive flex-shrink-0" />
                  <p className="text-xs font-medium text-app-ink truncate flex-1 group-hover:text-app-focus transition-colors">{m.title}</p>
                  <ChevronRight className="w-3.5 h-3.5 text-app-line-strong group-hover:text-app-focus flex-shrink-0" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <h3 className="text-sm font-semibold text-app-ink mb-4 flex items-center gap-2">
            <BookOpen className="w-4 h-4 text-app-focus" />
            Recent Activity
          </h3>
          {recentActivity.length === 0 ? (
            <p className="text-xs text-app-muted py-4">No activity yet.</p>
          ) : (
            <div className="space-y-2">
              {recentActivity.map((p, i) => {
                const mod = modules.find(m => m.slug === p.module_slug);
                const tabLabel = CRAFT_MODULE_TABS.find(t => t.key === p.article_slug)?.label || p.article_slug;
                const dateStr = p.completed_at
                  ? new Date(p.completed_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
                  : '';

                return (
                  <div key={p.id || i} className="flex items-center gap-3 px-4 py-2.5 bg-app-surface border border-app-line rounded-lg">
                    <CheckCircle2 className="w-3.5 h-3.5 text-app-positive flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-app-ink truncate">
                        <span className="font-medium">{tabLabel}</span>
                        {mod && <span className="text-app-muted"> in {mod.title}</span>}
                      </p>
                    </div>
                    <span className="text-[10px] text-app-muted flex-shrink-0">{dateStr}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <div className="bg-app-surface border border-app-line rounded-lg p-4">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-app-muted mb-1">{label}</p>
      <p className="text-2xl font-bold text-app-ink">{value}</p>
      <p className="text-[10px] text-app-muted">{sub}</p>
    </div>
  );
}
